import React, { useState, useRef, useEffect } from "react";
import { round, parseNumber } from "../shared/helpers";
import { wireResistance } from "../shared/wireData";
import "../style.css";

const circuitTypes = ["DC", "Single phase AC"];

const getWireSizes = material => Object.keys(wireResistance[material] || {});

const calculateVoltageDropDC = ({ distance, current, resistance }) => (2 * distance * current * resistance) / 1000;
const calculateVoltageDropAC = ({ distance, current, resistance, reactance, powerFactor }) => {
  const sinTheta = Math.sqrt(1 - powerFactor * powerFactor);
  return (2 * distance * current * (resistance * powerFactor + reactance * sinTheta)) / 1000;
};
const calculateVoltageDropPercent = ({ voltageDrop, systemVoltage }) => (voltageDrop / systemVoltage) * 100;
const calculatePowerLoss = ({ voltageDrop, current }) => voltageDrop * current;

const getNecStatus = voltageDropPercent => {
  if (voltageDropPercent > 5) return "Poor design";
  if (voltageDropPercent > 3) return "Warning";
  if (voltageDropPercent >= 2) return "Acceptable";
  return "Excellent";
};

const getStatusColor = status => {
  if (status === "Excellent") return "#0F6E56";
  if (status === "Acceptable") return "#854F0B";
  if (status === "Warning") return "#D97706";
  return "#A32D2D";
};

const getNextWire = (material, currentSize) => {
  const sizes = getWireSizes(material);
  const index = sizes.indexOf(currentSize);
  if (index < 0 || index === sizes.length - 1) return null;
  return sizes[index + 1];
};

export default function VoltageDropCalculator() {
  const [systemVoltage, setSystemVoltage] = useState(240);
  const [current, setCurrent] = useState(32);
  const [distance, setDistance] = useState(120);
  const [material, setMaterial] = useState("copper");
  const [wireSize, setWireSize] = useState(getWireSizes("copper")[1] || "12 AWG");
  const [circuitType, setCircuitType] = useState(circuitTypes[0]);
  const [powerFactor, setPowerFactor] = useState(0.9);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const handleCalculate = () => {
    setError("");
    const voltage = parseNumber(systemVoltage);
    const amps = parseNumber(current);
    const feet = parseNumber(distance);
    const pf = parseNumber(powerFactor);
    const conductor = wireResistance[material]?.[wireSize];

    if (voltage <= 0) {
      setError("Enter a valid system voltage greater than zero.");
      setResult(null);
      return;
    }
    if (amps <= 0) {
      setError("Enter a valid current (amps) greater than zero.");
      setResult(null);
      return;
    }
    if (feet <= 0) {
      setError("Enter a valid one-way wire length greater than zero.");
      setResult(null);
      return;
    }
    if (!Number.isFinite(pf) || pf < 0.1 || pf > 1) {
      setError("Power factor must be between 0.1 and 1.0.");
      setResult(null);
      return;
    }
    if (!conductor) {
      setError("Select a valid wire size for the chosen conductor material.");
      setResult(null);
      return;
    }

    const resistance = conductor.resistance;
    const reactance = conductor.reactance;
    let voltageDrop;
    let calculationMode = circuitType;

    if (circuitType === "DC") {
      voltageDrop = calculateVoltageDropDC({ distance: feet, current: amps, resistance });
    } else {
      voltageDrop = calculateVoltageDropAC({ distance: feet, current: amps, resistance, reactance, powerFactor: pf });
    }

    const voltageDropPercent = calculateVoltageDropPercent({ voltageDrop, systemVoltage: voltage });
    const powerLoss = calculatePowerLoss({ voltageDrop, current: amps });
    const status = getNecStatus(voltageDropPercent);
    const recommendation = voltageDropPercent > 3 ? getNextWire(material, wireSize) : null;

    setResult({
      voltageDrop: round(voltageDrop, 1),
      voltageDropPercent: round(voltageDropPercent, 2),
      powerLoss: round(powerLoss, 1),
      status,
      statusColor: getStatusColor(status),
      wireSize,
      material,
      circuitType,
      powerFactor: round(pf, 2),
      resistance: round(resistance, 3),
      reactance: round(reactance, 3),
      calculationMode,
      recommendation,
    });
  };

  const wireSizes = getWireSizes(material);

  return (
    <div className="se-wrap">
      {/* <div className="se-hero">
        <div className="se-hero-badge">Voltage Drop Calculator</div>
        <h1 className="se-hero-h1">Solar voltage drop calculator</h1>
        <p className="se-hero-p">Calculate voltage drop, power loss, and NEC wire sizing recommendations for solar electrical systems.</p>
      </div> */}

      <div className="se-card">
        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">System voltage (V)</label>
            <input
              className="se-input"
              type="number"
              value={systemVoltage}
              onChange={e => setSystemVoltage(e.target.value)}
              placeholder="240"
            />
          </div>
          <div className="se-field">
            <label className="se-label">Current (A)</label>
            <input
              className="se-input"
              type="number"
              value={current}
              onChange={e => setCurrent(e.target.value)}
              placeholder="32"
            />
          </div>
        </div>

        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">One-way wire length (ft)</label>
            <input
              className="se-input"
              type="number"
              value={distance}
              onChange={e => setDistance(e.target.value)}
              placeholder="120"
            />
          </div>
          <div className="se-field">
            <label className="se-label">Conductor material</label>
            <select className="se-input" value={material} onChange={e => {
              const nextMaterial = e.target.value;
              setMaterial(nextMaterial);
              setWireSize(getWireSizes(nextMaterial)[1] || "12 AWG");
            }}>
              <option value="copper">Copper</option>
              <option value="aluminum">Aluminum</option>
            </select>
          </div>
        </div>

        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">Wire size</label>
            <select className="se-input" value={wireSize} onChange={e => setWireSize(e.target.value)}>
              {wireSizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div className="se-field">
            <label className="se-label">Circuit type</label>
            <select className="se-input" value={circuitType} onChange={e => setCircuitType(e.target.value)}>
              {circuitTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="se-field-grid2">
          <div className="se-field">
            <label className="se-label">Power factor</label>
            <input
              className="se-input"
              type="number"
              min="0.1"
              max="1"
              step="0.01"
              value={powerFactor}
              onChange={e => setPowerFactor(e.target.value)}
            />
            <div className="se-res-sub">Typical solar inverter PF is 0.9–1.0</div>
          </div>
          {/* <div className="se-field mt-4" style={{ opacity: 0.85 }}>
            <label className="se-label">Engineering model</label>
            <div className="se-res-sub">AC uses resistance + reactance; DC uses pure resistance.</div>
          </div> */}
        </div>

        {error && <div className="se-err">{error}</div>}

        <div className="se-nav-row">
          <span />
          <button className="se-btn-next" onClick={handleCalculate}>Calculate voltage drop</button>
        </div>
      </div>

      {result && (
        <div ref={resultRef} className="se-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div className="se-res-label">NEC recommendation</div>
              <div className="se-res-val" style={{ color: result.statusColor }}>{result.status}</div>
            </div>
            {result.recommendation && (
              <div style={{ fontSize: 13, color: "#854F0B" }}>
                Recommended upgrade: <strong>{result.recommendation} {result.material}</strong>
              </div>
            )}
          </div>

          <div className="se-results-grid">
            <div className="se-res-card">
              <div className="se-res-label">Voltage drop</div>
              <div className="se-res-val">{result.voltageDrop} V</div>
            </div>
            <div className="se-res-card">
              <div className="se-res-label">Voltage drop %</div>
              <div className="se-res-val">{result.voltageDropPercent} %</div>
            </div>
            <div className="se-res-card">
              <div className="se-res-label">Power loss</div>
              <div className="se-res-val">{result.powerLoss} W</div>
            </div>
            <div className="se-res-card">
              <div className="se-res-label">Resistance used</div>
              <div className="se-res-val">{result.resistance} Ω/1000ft</div>
            </div>
            <div className="se-res-card">
              <div className="se-res-label">Reactance used</div>
              <div className="se-res-val">{result.reactance} Ω/1000ft</div>
            </div>
            <div className="se-res-card">
              <div className="se-res-label">Power factor</div>
              <div className="se-res-val">{result.powerFactor}</div>
            </div>
          </div>

          <div className="se-card" style={{ marginTop: 12, padding: 16, background: "#f8fafc" }}>
            <div className="se-res-label">Engineering information</div>
            <div className="se-field-grid2" style={{ marginTop: 8 }}>
              <div className="se-res-card" style={{ padding: 10, background: "#fff" }}>
                <div className="se-res-label">Conductor resistance</div>
                <div className="se-res-val">{result.resistance} Ω/1000ft</div>
              </div>
              <div className="se-res-card" style={{ padding: 10, background: "#fff" }}>
                <div className="se-res-label">Conductor reactance</div>
                <div className="se-res-val">{result.reactance} Ω/1000ft</div>
              </div>
              <div className="se-res-card" style={{ padding: 10, background: "#fff" }}>
                <div className="se-res-label">Calculation mode</div>
                <div className="se-res-val">{result.calculationMode}</div>
              </div>
              <div className="se-res-card" style={{ padding: 10, background: "#fff" }}>
                <div className="se-res-label">Power factor</div>
                <div className="se-res-val">{result.powerFactor}</div>
              </div>
            </div>
          </div>

          <div className="se-disclaimer">
            Estimated voltage drop for installer guidance. Confirm final sizing using manufacturer and NEC tables.
          </div>
        </div>
      )}
    </div>
  );
}
